import { Auth, UserInfo } from '@spotify-internal/oauth-silent-authentication';
import React, { createContext, useMemo } from 'react';
import { useContext } from 'react';
import { useEffect, useState } from 'react';
import { useCookie } from 'react-use';
import { getSentry } from '../../chrome/sentry/init';
import { getAuth } from './auth';

export enum LoginState {
  AWAITING_LOGIN,
  LOGGEDIN,
  LOGIN_FAILED,
}

export type UserContextValue = {
  loginState: LoginState;
  userInfo?: UserInfo;
  login?: () => void;
  logout?: () => void;
  getToken?: () => Promise<string>;
};

// exported for testing
export const UserContext = createContext<UserContextValue>({
  loginState: LoginState.AWAITING_LOGIN,
});

const getLoginState = async (auth: Auth) => {
  const Sentry = getSentry();

  try {
    const token = await auth.getToken();
    if (token) {
      return LoginState.LOGGEDIN;
    }
  } catch (error) {
    if (error.error === 'login_required') {
      // redirects back to homepage on success, will set sp cookies used for silent auth
      auth!.login(`${window.location.origin}`);
      return LoginState.AWAITING_LOGIN;
    }
    if ('error' in error) {
      Sentry.captureException(
        new Error(`${error.error}: ${error.error_description}`),
      );
    } else {
      Sentry.captureException(error);
    }
    return LoginState.LOGIN_FAILED;
  }
  return LoginState.LOGIN_FAILED;
};

const getUserInfo = async (auth: Auth) => {
  const Sentry = getSentry();
  try {
    return await auth.getUserInfo();
  } catch (error) {
    if ('error' in error) {
      Sentry.captureException(
        new Error(`${error.error}: ${error.error_description}`),
      );
    } else {
      Sentry.captureException(error);
    }
    return undefined;
  }
};

export const UserProvider = ({
  children,
  initialUserInfo,
}: React.PropsWithChildren<{ initialUserInfo?: UserInfo }>) => {
  const auth = getAuth();
  const [, updateCookie, deleteCookie] = useCookie('sp_userinfo');
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>(
    initialUserInfo,
  );
  const [loginState, setLoginState] = useState<LoginState>(
    LoginState.AWAITING_LOGIN,
  );

  useEffect(() => {
    const Sentry = getSentry();

    if (auth && loginState === LoginState.AWAITING_LOGIN) {
      getLoginState(auth)
        .then(state => {
          setLoginState(state);
          return state;
        })
        .then(state => {
          if (state === LoginState.LOGGEDIN) {
            return getUserInfo(auth);
          }
          return Promise.resolve(undefined);
        })
        .then(value => {
          setUserInfo(value);
          if (value) {
            updateCookie(JSON.stringify(value));
          }
        })
        .catch(e => {
          Sentry.captureException(e);
        });
    }
  }, [loginState, updateCookie, auth]);
  const login = useMemo(
    () => () => auth && auth.login(`${window.location.origin}`),
    [auth],
  );
  const getToken = useMemo(
    () => async () => (auth && (await auth.getToken())) || '',
    [auth],
  );
  const logout = useMemo(
    () => () => {
      deleteCookie();
      return auth && auth.logout(`${window.location.origin}`);
    },
    [auth, deleteCookie],
  );

  return (
    <UserContext.Provider
      value={{
        userInfo,
        loginState,
        login,
        getToken,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  return useContext(UserContext);
};
